import { Link, useParams } from "react-router-dom";
import { Chip } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { Panel, EmptyState, LoadingPanel, ErrorPanel } from "../../components/ui";
import { partnerQueryKeys } from "../../api/queryKeys";
import { fetchPartnerCourse } from "../../api/endpoints/workspace";
import usePartnerQuery from "../../hooks/usePartnerQuery";
import { courseDuration, courseMoney, percent } from "./courseHelpers";

const courseKey = (courseId) => [...partnerQueryKeys.all, "course", courseId];

const Fact = ({ label, value }) => (
  <div className="bg-white px-4 py-3">
    <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 m-0">
      {label}
    </dt>
    <dd className="text-sm font-semibold text-slate-900 m-0 mt-0.5 tabular-nums">{value || "—"}</dd>
  </div>
);

const Requirement = ({ label, text }) =>
  text ? (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 m-0 mb-1">
        {label}
      </p>
      <p className="text-sm text-slate-700 m-0 whitespace-pre-line leading-relaxed">{text}</p>
    </div>
  ) : null;

const Line = ({ label, value }) =>
  value ? (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="text-sm font-medium text-slate-900 tabular-nums">{value}</span>
    </div>
  ) : null;

/**
 * One course opened in full, so a partner can quote it without calling the office: the fee in the
 * state the student is in, what the student must bring, when it starts, and who teaches it.
 */
const CourseDetailPage = () => {
  const { courseId } = useParams();
  const { data, isLoading, isError, refetch } = usePartnerQuery(courseKey(courseId), () =>
    fetchPartnerCourse(courseId)
  );

  if (isLoading) return <LoadingPanel label="Opening the course…" />;
  if (isError) {
    return (
      <ErrorPanel
        description="The course could not be opened. It may have been withdrawn from the catalogue."
        action={
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white"
            >
              Try again
            </button>
            <Link
              to="/partner/courses"
              className="text-xs font-semibold text-rose-900 no-underline"
            >
              Back to courses
            </Link>
          </div>
        }
      />
    );
  }

  const course = data || {};
  const university = course.university || {};
  const place = [university.state, university.country].filter(Boolean).join(", ");
  const onshoreFees = course.onshoreFees || [];
  const scholarships = course.scholarships || [];
  const intakes = course.intakes || [];
  const hasRequirements = Boolean(
    course.englishLanguageRequirements || course.academicRequirements || course.otherRequirements
  );

  return (
    <div>
      <Link
        to="/partner/courses"
        className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 no-underline mb-3"
      >
        <ArrowBack sx={{ fontSize: 14 }} />
        Back to courses
      </Link>

      <header className="rounded-2xl border border-slate-200 bg-white px-5 py-4 mb-3">
        <h1 className="text-lg font-semibold text-slate-900 m-0 leading-snug">
          {course.courseName || "Untitled course"}
        </h1>
        <p className="text-sm text-slate-500 m-0 mt-1">
          {university.name || "University not set"}
          {place ? ` · ${place}` : ""}
        </p>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {courseDuration(course.duration) ? (
            <Chip
              size="small"
              label={courseDuration(course.duration)}
              sx={{ height: 22, fontSize: 11 }}
            />
          ) : null}
          {course.courseCode ? (
            <Chip
              size="small"
              variant="outlined"
              label={course.courseCode}
              sx={{ height: 22, fontSize: 11 }}
            />
          ) : null}
          {course.maxScholarship ? (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              label={`Up to ${percent(course.maxScholarship)} scholarship`}
              sx={{ height: 22, fontSize: 11 }}
            />
          ) : null}
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 space-y-3">
          <Panel title="Course overview">
            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-slate-100 m-0 -mx-4 -mb-4">
              <Fact label="Level" value={course.level || (course.programLevels || []).join(", ")} />
              <Fact label="Duration" value={courseDuration(course.duration)} />
              <Fact label="Semesters" value={course.noOfSem} />
              <Fact label="Field" value={course.field} />
            </dl>
          </Panel>

          <Panel title="Fees">
            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-slate-100 m-0 -mx-4">
              <Fact label="Per sem" value={courseMoney(course.feePerSem)} />
              <Fact label="Total" value={courseMoney(course.totalFees)} />
              <Fact label="Enrolment" value={courseMoney(course.enrollmentFee)} />
              <Fact label="Material" value={courseMoney(course.materialFee)} />
            </dl>
            {course.feeAfterScholarship || course.scholarshipPercent ? (
              <div className="mt-4 space-y-1.5">
                <Line label="Scholarship" value={percent(course.scholarshipPercent)} />
                <Line
                  label="Fee after scholarship"
                  value={courseMoney(course.feeAfterScholarship)}
                />
              </div>
            ) : null}
          </Panel>

          {onshoreFees.length > 0 ? (
            <Panel title="Fees by state">
              <div className="space-y-3">
                {onshoreFees.map((fee) => (
                  <div
                    key={fee.state || fee.totalFees}
                    className="rounded-xl border border-slate-200 px-4 py-3"
                  >
                    <p className="text-sm font-semibold text-slate-900 m-0 mb-2">
                      {fee.state || "Any state"}
                    </p>
                    <div className="space-y-1.5">
                      <Line label="Per semester" value={courseMoney(fee.feePerSem)} />
                      <Line label="Total fees" value={courseMoney(fee.totalFees)} />
                      <Line label="Scholarship" value={percent(fee.scholarshipPercent)} />
                      <Line
                        label="Fee after scholarship"
                        value={courseMoney(fee.feeAfterScholarship)}
                      />
                      <Line label="Discount" value={percent(fee.discountPercent)} />
                    </div>
                    {fee.note ? (
                      <p className="text-xs text-slate-600 m-0 mt-2 whitespace-pre-line">
                        {fee.note}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            </Panel>
          ) : null}

          {course.offshoreFees ? (
            <Panel title="Offshore fees">
              <div className="space-y-1.5">
                <Line label="Per semester" value={courseMoney(course.offshoreFees.feePerSem)} />
                <Line label="Total fees" value={courseMoney(course.offshoreFees.totalFees)} />
                <Line
                  label="Enrolment fee"
                  value={courseMoney(course.offshoreFees.enrollmentFee)}
                />
                <Line label="Material fee" value={courseMoney(course.offshoreFees.materialFee)} />
                <Line label="Scholarship" value={percent(course.offshoreFees.scholarshipPercent)} />
                <Line
                  label="Fee after scholarship"
                  value={courseMoney(course.offshoreFees.feeAfterScholarship)}
                />
                <Line label="Discount" value={percent(course.offshoreFees.discountPercent)} />
              </div>
              {course.offshoreFees.note ? (
                <p className="text-xs text-slate-600 m-0 mt-3 whitespace-pre-line">
                  {course.offshoreFees.note}
                </p>
              ) : null}
            </Panel>
          ) : null}

          <Panel title="Requirements">
            {hasRequirements ? (
              <div className="space-y-4">
                <Requirement label="English language" text={course.englishLanguageRequirements} />
                <Requirement label="Academic" text={course.academicRequirements} />
                <Requirement label="Other" text={course.otherRequirements} />
              </div>
            ) : (
              <EmptyState
                title="No requirements recorded"
                description="Ask the office before you quote entry conditions."
              />
            )}
          </Panel>

          {scholarships.length > 0 ? (
            <Panel title="Scholarships">
              <div className="space-y-2">
                {scholarships.map((row, index) => (
                  <div
                    key={`${row.intake}-${index}`}
                    className="rounded-xl border border-slate-200 px-4 py-3"
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-sm font-medium text-slate-900">
                        {row.intake || "Any intake"}
                      </span>
                      <span className="text-sm font-semibold text-emerald-700">
                        {percent(row.scholarshipPercent) || "—"}
                      </span>
                    </div>
                    {row.note ? (
                      <p className="text-xs text-slate-600 m-0 mt-1 whitespace-pre-line">
                        {row.note}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            </Panel>
          ) : null}
        </div>

        <div className="space-y-3">
          <Panel title="Intakes">
            {intakes.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {intakes.map((intake) => (
                  <span
                    key={intake}
                    className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600"
                  >
                    {intake}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-500 m-0">No intakes listed.</p>
            )}
          </Panel>

          <Panel title="University">
            <div className="space-y-1.5">
              <Line label="Name" value={university.name} />
              <Line label="Country" value={university.country} />
              <Line label="State" value={university.state} />
              <Line label="ABN" value={university.abn} />
              <Line
                label="Courses available"
                value={university.coursesAvailable ? String(university.coursesAvailable) : null}
              />
            </div>
            {course.websiteLink ? (
              <a
                href={course.websiteLink}
                target="_blank"
                rel="noreferrer"
                className="inline-block mt-3 text-xs font-semibold text-teal-700 no-underline"
              >
                Course page on the university site
              </a>
            ) : null}
          </Panel>
        </div>
      </div>
    </div>
  );
};

export default CourseDetailPage;
